import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { 
  Upload, FileText, Loader2, CheckCircle, AlertTriangle, Sparkles,
  ArrowRight, RotateCcw, Save, Clock
} from "lucide-react";
import { useDropzone } from "react-dropzone";
import VehicleValidationDialog from "../../components/engagements/VehicleValidationDialog";

const API = process.env.REACT_APP_BACKEND_URL;

// Steps
const STEPS = [
  { id: 1, label: "Dépôt", icon: Upload },
  { id: 2, label: "OCR IA", icon: Sparkles },
  { id: 3, label: "Analyse", icon: FileText },
  { id: 4, label: "Enregistré", icon: CheckCircle }
];

// Categories - all available categories
const CATEGORIES = [
  { value: "pret", label: "🏦 Prêt bancaire" },
  { value: "credit", label: "💳 Crédit" },
  { value: "assurance", label: "🛡️ Assurance" },
  { value: "telephonie", label: "📱 Téléphonie" },
  { value: "leasing", label: "🚗 Leasing" },
  { value: "saas", label: "☁️ SaaS / Logiciels" },
  { value: "energie", label: "💡 Énergie" },
  { value: "materiel", label: "🖨️ Matériel" },
  { value: "maintenance", label: "🔧 Maintenance" },
  { value: "location", label: "🏢 Location" },
  { value: "services", label: "💼 Services" },
  { value: "abonnement", label: "📰 Abonnement" },
  { value: "autre", label: "📄 Autre" }
];

// Modes de résiliation
const MODES_RESILIATION = [
  { value: "lrar", label: "LRAR (Lettre recommandée)" },
  { value: "lettre_simple", label: "Lettre simple" },
  { value: "email", label: "Email" },
  { value: "autre", label: "Autre" }
];

// Format date for input
const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toISOString().split("T")[0];
  } catch {
    return dateStr;
  }
};

export default function ImportContrat() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [pendingContractId, setPendingContractId] = useState(null);
  
  // Form data (editable after extraction)
  const [formData, setFormData] = useState({
    nom_contrat: "",
    prestataire: "",
    categorie: "autre",
    numero_contrat: "",
    montant_mensuel_ht: "",
    montant_annuel_ht: "",
    date_debut: "",
    date_echeance: "",
    duree_engagement_mois: "",
    delai_preavis_jours: "",
    date_limite_preavis: "",
    date_resiliation_possible: "",
    mode_resiliation: "",
    reconduction_tacite: true,
    resume_ia: "",
    adresse_prestataire: "",
    email_prestataire: "",
    telephone_prestataire: ""
  });
  const [clauses, setClauses] = useState([]);

  // Dropzone
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    const uploadedFile = acceptedFiles[0];
    setFile(uploadedFile);
    setCurrentStep(2);
    setLoading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", uploadedFile);
      
      const res = await axios.post(`${API}/api/engagements/ocr/extract`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      if (res.data.success) {
        const extracted = res.data.extraction;
        setExtraction(extracted);
        setFileData(res.data.file_data);
        
        // Pre-fill form with extracted data
        setFormData({
          nom_contrat: extracted.nom_contrat || extracted.type_contrat || "",
          prestataire: extracted.nom_prestataire || "",
          categorie: extracted.type_contrat || "autre",
          numero_contrat: extracted.numero_contrat || "",
          montant_mensuel_ht: extracted.montant_mensuel_ht || "",
          montant_annuel_ht: extracted.montant_annuel_ht || "",
          date_debut: formatDateForInput(extracted.date_debut) || "",
          date_echeance: formatDateForInput(extracted.date_echeance) || "",
          duree_engagement_mois: extracted.duree_engagement_mois || "",
          delai_preavis_jours: extracted.delai_preavis_jours || "",
          date_limite_preavis: formatDateForInput(extracted.date_limite_preavis) || "",
          date_resiliation_possible: formatDateForInput(extracted.date_resiliation_possible) || "",
          mode_resiliation: extracted.mode_resiliation || "",
          reconduction_tacite: extracted.reconduction_tacite !== false,
          resume_ia: extracted.resume_contrat || "",
          adresse_prestataire: extracted.adresse_prestataire || "",
          email_prestataire: extracted.email_prestataire || "",
          telephone_prestataire: extracted.telephone_prestataire || ""
        });
        
        // Set clauses
        if (extracted.clauses_importantes && extracted.clauses_importantes.length > 0) {
          setClauses(extracted.clauses_importantes.map((c, i) => ({
            type: `clause_${i}`,
            description: c
          })));
        }
        
        setCurrentStep(3);
        toast.success("Document analysé avec succès");
      }
    } catch (error) {
      console.error("OCR Error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'analyse OCR");
      setCurrentStep(1);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    maxFiles: 1,
    disabled: loading || currentStep > 1
  });

  // Handle form changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate date_limite_preavis
    if ((field === "date_echeance" || field === "delai_preavis_jours") && formData.date_echeance && formData.delai_preavis_jours) {
      const echeance = new Date(field === "date_echeance" ? value : formData.date_echeance);
      const delai = parseInt(field === "delai_preavis_jours" ? value : formData.delai_preavis_jours) || 0;
      if (!isNaN(echeance.getTime()) && delai > 0) {
        const preavisDate = new Date(echeance);
        preavisDate.setDate(preavisDate.getDate() - delai);
        setFormData(prev => ({ ...prev, date_limite_preavis: preavisDate.toISOString().split("T")[0] }));
      }
    }
    
    // Auto-calculate montant_annuel
    if (field === "montant_mensuel_ht" && value) {
      setFormData(prev => ({ ...prev, montant_annuel_ht: parseFloat(value) * 12 }));
    }
  };

  // Save contract
  const handleSave = async () => {
    if (!formData.nom_contrat || !formData.prestataire) {
      toast.error("Le nom du contrat et le prestataire sont obligatoires");
      return;
    }
    
    setLoading(true);
    try {
      // Helper: convert "" → null (Pydantic rejects "" for Optional[date] / Optional[Enum])
      const emptyToNull = (v) => (v === "" || v === undefined ? null : v);

      const payload = {
        nom_contrat: formData.nom_contrat,
        prestataire: formData.prestataire,
        categorie: formData.categorie || "autre",
        numero_contrat: emptyToNull(formData.numero_contrat),
        montant_mensuel_ht: parseFloat(formData.montant_mensuel_ht) || null,
        montant_annuel_ht: parseFloat(formData.montant_annuel_ht) || null,
        date_debut: emptyToNull(formData.date_debut),
        date_echeance: emptyToNull(formData.date_echeance),
        duree_engagement_mois: parseInt(formData.duree_engagement_mois) || null,
        delai_preavis_jours: parseInt(formData.delai_preavis_jours) || null,
        date_limite_preavis: emptyToNull(formData.date_limite_preavis),
        date_resiliation_possible: emptyToNull(formData.date_resiliation_possible),
        mode_resiliation: emptyToNull(formData.mode_resiliation),
        reconduction_tacite: formData.reconduction_tacite !== false,
        resume_ia: emptyToNull(formData.resume_ia),
        adresse_prestataire: emptyToNull(formData.adresse_prestataire),
        email_prestataire: emptyToNull(formData.email_prestataire),
        telephone_prestataire: emptyToNull(formData.telephone_prestataire),
        clauses: clauses,
        // Champs véhicule extraits par l'OCR (pour synchro avec le Parc Auto)
        is_vehicle_contract: !!extraction?.is_vehicle_contract,
        vehicle_info: extraction?.vehicle_info || null,
        vehicle_contract_info: extraction?.vehicle_contract_info || null,
        fichier_original_url: fileData?.filename || null,
        fichier_original_base64: fileData?.base64 || null,
        fichier_original_content_type: fileData?.content_type || null
      };
      
      const res = await axios.post(`${API}/api/engagements/contrats`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Contrat enregistré avec succès !");

      // Si l'OCR a détecté un contrat véhicule → propose la validation Parc Auto
      if (extraction?.is_vehicle_contract) {
        setPendingContractId(res.data.id);
        setVehicleDialogOpen(true);
      } else {
        // Navigate immediately to contract detail
        navigate(`/engagements/contrats/${res.data.id}`);
      }
      
    } catch (error) {
      console.error("Save error:", error);
      // Pydantic renvoie parfois un array d'objets ; on le convertit en string lisible
      const detail = error.response?.data?.detail;
      let msg = "Erreur lors de l'enregistrement";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail
          .map((e) => `${(e.loc || []).slice(-1)[0] || "champ"}: ${e.msg || "invalide"}`)
          .join(" • ");
      } else if (detail && typeof detail === "object") {
        msg = detail.msg || JSON.stringify(detail);
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Reset
  const handleReset = () => {
    setFile(null);
    setFileData(null);
    setExtraction(null);
    setFormData({
      nom_contrat: "",
      prestataire: "",
      categorie: "autre",
      numero_contrat: "",
      montant_mensuel_ht: "",
      montant_annuel_ht: "",
      date_debut: "",
      date_echeance: "",
      duree_engagement_mois: "",
      delai_preavis_jours: "",
      date_limite_preavis: "",
      date_resiliation_possible: "",
      mode_resiliation: "",
      reconduction_tacite: true,
      resume_ia: "",
      adresse_prestataire: "",
      email_prestataire: "",
      telephone_prestataire: ""
    });
    setClauses([]);
    setCurrentStep(1);
  };

  // Check if preavis is urgent
  const isPreavisUrgent = () => {
    if (!formData.date_limite_preavis) return false;
    const preavis = new Date(formData.date_limite_preavis);
    const today = new Date();
    const diffDays = Math.floor((preavis - today) / (1000 * 60 * 60 * 24));
    return diffDays <= 60;
  };

  return (
    <div className="p-6 space-y-6" data-testid="import-contrat">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Importer un contrat</h1>
        <p className="text-slate-500 mt-1">Déposez votre contrat pour l'analyser automatiquement</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isComplete = currentStep > step.id;
          
          return (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                isComplete ? "bg-green-100 text-green-700" :
                isActive ? "bg-blue-100 text-blue-700" :
                "bg-slate-100 text-slate-400"
              }`}>
                {isComplete ? (
                  <CheckCircle className="w-4 h-4" />
                ) : isActive && loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">{step.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <ArrowRight className={`w-4 h-4 mx-2 ${currentStep > step.id ? "text-green-500" : "text-slate-300"}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Step 1: Upload */}
      {currentStep === 1 && (
        <Card>
          <CardContent className="p-8">
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer ${
                isDragActive ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400 hover:bg-slate-50"
              }`}
            >
              <input {...getInputProps()} />
              <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragActive ? "text-blue-500" : "text-slate-400"}`} />
              <p className="text-lg font-medium text-slate-700">
                {isDragActive ? "Déposez le fichier ici" : "Glissez-déposez votre contrat"}
              </p>
              <p className="text-sm text-slate-500 mt-2">ou cliquez pour sélectionner un fichier</p>
              <p className="text-xs text-slate-400 mt-4">Formats acceptés : PDF, PNG, JPG</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Loading OCR */}
      {currentStep === 2 && loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="w-12 h-12 mx-auto text-blue-500 animate-spin mb-4" />
            <p className="text-lg font-medium text-slate-700">Analyse en cours...</p>
            <p className="text-sm text-slate-500 mt-2">L'IA extrait les informations de votre contrat</p>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Review & Edit */}
      {currentStep === 3 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: AI Summary */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  Résumé IA
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 whitespace-pre-line">
                  {formData.resume_ia || "Aucun résumé disponible"}
                </p>
                
                {clauses.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium text-slate-700 mb-2">Clauses importantes :</p>
                    <ul className="space-y-1">
                      {clauses.map((clause, i) => (
                        <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                          <span className="text-blue-500">•</span>
                          {clause.description}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Urgency Alert */}
            {isPreavisUrgent() && (
              <Alert className="bg-red-50 border-red-200">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  <strong>Attention !</strong> La date limite de préavis est dans moins de 60 jours.
                </AlertDescription>
              </Alert>
            )}

            {/* File info */}
            {file && (
              <Card className="bg-slate-50">
                <CardContent className="p-4 flex items-center gap-3">
                  <FileText className="w-8 h-8 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{file.name}</p>
                    <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} Ko</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Editable Form */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Données extraites</CardTitle>
              <p className="text-sm text-slate-500">Vérifiez et modifiez les informations si nécessaire</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prestataire *</Label>
                  <Input
                    value={formData.prestataire}
                    onChange={(e) => handleChange("prestataire", e.target.value)}
                    placeholder="Nom du prestataire"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom du contrat *</Label>
                  <Input
                    value={formData.nom_contrat}
                    onChange={(e) => handleChange("nom_contrat", e.target.value)}
                    placeholder="Ex: Multirisque Pro"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>N° Contrat/Police</Label>
                  <Input
                    value={formData.numero_contrat}
                    onChange={(e) => handleChange("numero_contrat", e.target.value)}
                    placeholder="Ex: AXA-PRO-2025-001234"
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select value={formData.categorie} onValueChange={(v) => handleChange("categorie", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mode de résiliation</Label>
                  <Select value={formData.mode_resiliation || ""} onValueChange={(v) => handleChange("mode_resiliation", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {MODES_RESILIATION.map(mode => (
                        <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Amounts */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant mensuel HT (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.montant_mensuel_ht}
                    onChange={(e) => handleChange("montant_mensuel_ht", e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Montant annuel HT (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.montant_annuel_ht}
                    onChange={(e) => handleChange("montant_annuel_ht", e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Date de début</Label>
                  <Input
                    type="date"
                    value={formData.date_debut}
                    onChange={(e) => handleChange("date_debut", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date d'échéance</Label>
                  <Input
                    type="date"
                    value={formData.date_echeance}
                    onChange={(e) => handleChange("date_echeance", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Durée engagement (mois)</Label>
                  <Input
                    type="number"
                    value={formData.duree_engagement_mois}
                    onChange={(e) => handleChange("duree_engagement_mois", e.target.value)}
                    placeholder="12"
                  />
                </div>
              </div>

              {/* Preavis */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Délai de préavis (jours)</Label>
                  <Input
                    type="number"
                    value={formData.delai_preavis_jours}
                    onChange={(e) => handleChange("delai_preavis_jours", e.target.value)}
                    placeholder="60"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Date limite préavis
                    {isPreavisUrgent() && <Clock className="w-4 h-4 text-red-500" />}
                  </Label>
                  <Input
                    type="date"
                    value={formData.date_limite_preavis}
                    onChange={(e) => handleChange("date_limite_preavis", e.target.value)}
                    className={isPreavisUrgent() ? "border-red-300 bg-red-50" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date résiliation possible</Label>
                  <Input
                    type="date"
                    value={formData.date_resiliation_possible}
                    onChange={(e) => handleChange("date_resiliation_possible", e.target.value)}
                  />
                </div>
              </div>

              {/* Address & Contact */}
              <div className="space-y-2">
                <Label>Adresse du prestataire</Label>
                <Textarea
                  value={formData.adresse_prestataire}
                  onChange={(e) => handleChange("adresse_prestataire", e.target.value)}
                  placeholder="Adresse complète pour l'envoi des courriers"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Email du prestataire
                    <span className="text-xs text-blue-500 font-normal">(pour envoi résiliation par email)</span>
                  </Label>
                  <Input
                    type="email"
                    value={formData.email_prestataire}
                    onChange={(e) => handleChange("email_prestataire", e.target.value)}
                    placeholder="resiliation@prestataire.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone du prestataire</Label>
                  <Input
                    type="tel"
                    value={formData.telephone_prestataire}
                    onChange={(e) => handleChange("telephone_prestataire", e.target.value)}
                    placeholder="01 23 45 67 89"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={handleReset}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Recommencer
                </Button>
                <Button onClick={handleSave} disabled={loading}>
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer le contrat
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 4: Success */}
      {currentStep === 4 && (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-xl font-semibold text-slate-900">Contrat enregistré !</p>
            <p className="text-slate-500 mt-2">Redirection vers la fiche du contrat...</p>
          </CardContent>
        </Card>
      )}

      {/* Dialog validation véhicule Parc Auto — s'affiche uniquement si l'OCR a détecté un contrat véhicule */}
      <VehicleValidationDialog
        open={vehicleDialogOpen}
        onOpenChange={(open) => {
          setVehicleDialogOpen(open);
          if (!open && pendingContractId) {
            navigate(`/engagements/contrats/${pendingContractId}`);
          }
        }}
        engagementId={pendingContractId}
        extractedVehicleInfo={extraction?.vehicle_info || {}}
        extractedContractInfo={extraction?.vehicle_contract_info || {}}
        token={token}
        onLinked={() => { /* dialog handles close + navigate */ }}
        onSkip={() => { /* Just close, navigate handled by onOpenChange */ }}
      />
    </div>
  );
}
